import { useState, useEffect, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { Search, Plus, AlertTriangle } from "lucide-react";
import { api } from "../api/client";
import { formatDate } from "../data/format";

const EXPIRY_FILTERS = [
  { value: "", label: "All" },
  { value: "1", label: "Expiring in 1 day" },
  { value: "7", label: "Expiring in 7 days" },
  { value: "14", label: "Expiring in 14 days" },
];

function urgencyColor(days) {
  if (days === null || days === undefined) return "var(--ink-400)";
  if (days <= 1) return "var(--red-700)";
  if (days <= 7) return "var(--amber-700)";
  return "var(--ink-600)";
}

export default function SerialManagement() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [expiringWithin, setExpiringWithin] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ current_code: "", code_expiry: "" });
  const [saving, setSaving] = useState(false);
  const debounceRef = useRef(null);

  const load = useCallback(() => {
    setLoading(true);
    api
      .request(`/serials/`, { params: { search, expiring_within: expiringWithin || undefined, limit: 50 } })
      .then((data) => {
        setRows(data.results);
        setError("");
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [search, expiringWithin]);

  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(load, 250);
    return () => clearTimeout(debounceRef.current);
  }, [load]);

  function startEdit(row) {
    setEditingId(row.id);
    setEditForm({ current_code: row.current_code || "", code_expiry: row.code_expiry || "" });
  }

  async function saveEdit(id) {
    setSaving(true);
    try {
      await api.request(`/serials/${id}/`, { method: "PATCH", body: editForm });
      setEditingId(null);
      load();
    } catch (e) {
      alert(e.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div>
      <div className="pl-page-title">Serial Management</div>
      <div className="pl-page-sub">GNSS device serials, activation codes, and expiry — tied to payment plans.</div>

      <div className="pl-customers-toolbar" style={{ marginTop: 22, flexWrap: "wrap", gap: 10 }}>
        <div className="pl-searchbar" style={{ marginBottom: 0 }}>
          <Search size={14} color="var(--ink-400)" />
          <input placeholder="Search serial, client, customer" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <select value={expiringWithin} onChange={(e) => setExpiringWithin(e.target.value)} className="pl-toolbar-select">
          {EXPIRY_FILTERS.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
        </select>
      </div>

      {loading && <div className="pl-empty">Loading...</div>}
      {error && <div className="pl-empty">{error}</div>}

      {!loading && rows.length === 0 && <div className="pl-empty">No serials match.</div>}

      {rows.length > 0 && (
        <div style={{ marginTop: 18, display: "flex", flexDirection: "column", gap: 10 }}>
          {rows.map((s) => (
            <div key={s.id} className="pl-card">
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, flexWrap: "wrap" }}>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 14 }}>{s.serial_number}</div>
                  <div className="pl-meta">
                    {s.client_name || "(no client name)"}
                    {s.customer_name && (
                      <> — linked to <Link to={`/customers/${s.customer_id}`} style={{ color: "var(--blue-700)" }}>{s.customer_name}</Link></>
                    )}
                    {!s.plan_id && <span style={{ color: "var(--amber-700)" }}> — unlinked</span>}
                  </div>
                </div>
                {s.days_until_expiry !== null && (
                  <div style={{ display: "flex", alignItems: "center", gap: 4, color: urgencyColor(s.days_until_expiry), fontWeight: 700, fontSize: 13 }}>
                    {s.days_until_expiry <= 7 && <AlertTriangle size={13} />}
                    {s.days_until_expiry <= 0 ? "Expired" : `${s.days_until_expiry}d left`}
                  </div>
                )}
              </div>

              {editingId === s.id ? (
                <div style={{ marginTop: 12, padding: 12, background: "var(--blue-50)", borderRadius: 8, display: "flex", gap: 10, flexWrap: "wrap" }}>
                  <input
                    placeholder="Activation code"
                    value={editForm.current_code}
                    onChange={(e) => setEditForm((f) => ({ ...f, current_code: e.target.value }))}
                    style={{ flex: "1 1 160px" }}
                  />
                  <input
                    type="date"
                    value={editForm.code_expiry}
                    onChange={(e) => setEditForm((f) => ({ ...f, code_expiry: e.target.value }))}
                  />
                  <button className="pl-btn pl-btn-confirm" disabled={saving} onClick={() => saveEdit(s.id)}>
                    {saving ? "Saving..." : "Save"}
                  </button>
                  <button className="pl-btn pl-btn-ghost" onClick={() => setEditingId(null)}>Cancel</button>
                </div>
              ) : (
                <div style={{ display: "flex", justifyContent: "space-between", marginTop: 10, fontSize: 13 }}>
                  <span>Code: <strong>{s.current_code || "\u2014"}</strong></span>
                  <span>Expires: <strong>{s.code_expiry ? formatDate(s.code_expiry) : "\u2014"}</strong></span>
                  <button className="pl-btn pl-btn-ghost" onClick={() => startEdit(s)}>Edit</button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}